import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getSignPdfSetupChecklist,
  isSignPdfBlocksUnlocked,
  SIGN_PDF_WORKFLOW_STEPS,
} from "./signPdfWorkflow";
import {
  canConfirmCurrentPage,
  connectorClassForRailState,
  getSignPdfPageRailState,
  railStateClassName,
  shouldSpotlightPageThumb,
  SIGN_PDF_RAIL_STATES,
} from "./signPdfPageRail";
import {
  getSignPdfTemplate,
  SIGN_PDF_FIELD_KINDS,
  SIGN_PDF_FIELD_OPTIONS,
} from "./signPdfPlacements";
import SignPdfFieldPaletteRow from "./SignPdfFieldPaletteRow";
import SignPdfBlocksPanel from "./SignPdfBlocksPanel";
import SignPdfPagePopdown from "./SignPdfPagePopdown";
import SignPdfReviewPopdown from "./SignPdfReviewPopdown";
import SignPdfFinishPopdown from "./SignPdfFinishPopdown";
import "./SignPdfSignToolbar.css";
import "./SignPdfFinishPopdown.css";
import "./SignPdfReviewPopdown.css";
import "./SignPdfPopdownPointer.css";
import "./SignPdfBlocksPanel.css";

const FIELDS_MENU_WIDTH = 340;
const BLOCKS_MENU_WIDTH = 520;

/** Single spotlight — clockwise flowing border (page | review | finish). */
function RailThumbFrame({ spotlight = null, children }) {
  return (
    <span
      className={`sp-sign-rail__thumb-frame${
        spotlight ? ` is-spotlight is-spotlight--${spotlight}` : ""
      }`}
    >
      {children}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <path
        d="M2.5 6l2.2 2.2 4.8-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ReviewEyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12a4.5 4.5 0 100-9 4.5 4.5 0 000 9z"
        fill="currentColor"
      />
    </svg>
  );
}

function FieldsMenu({
  session,
  workflowStep,
  disabled,
  onEditSignature,
  onEditInitials,
  onChooseFormat,
  onPlaceField,
  canPlaceField,
  highlightSetup = false,
}) {
  const checklist = getSignPdfSetupChecklist(session);
  const setupPhase =
    workflowStep === SIGN_PDF_WORKFLOW_STEPS.SETUP ||
    workflowStep === SIGN_PDF_WORKFLOW_STEPS.FORMAT;
  const fieldsEnabled =
    checklist.complete && workflowStep !== SIGN_PDF_WORKFLOW_STEPS.REVIEW;
  const progressPct = Math.round(
    (checklist.doneCount / checklist.totalRequired) * 100
  );

  return (
    <div className="sp-fields-menu">
      {setupPhase || !checklist.complete ? (
        <div className="sp-fields-menu__setup">
          <p className="sp-fields-menu__setup-title">Setup progress</p>
          <div className="sp-fields-menu__progress-bar">
            <div
              className="sp-fields-menu__progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {checklist.required.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`sp-fields-menu__check${item.done ? " is-done" : ""}`}
              onClick={() => onEditSignature?.()}
              disabled={disabled}
            >
              <span className="sp-fields-menu__check-mark">
                {item.done ? <CheckIcon /> : null}
              </span>
              {item.label}
              {item.required ? " *" : ""}
            </button>
          ))}
          <p className="sp-fields-menu__optional">Optional</p>
          {checklist.optional.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`sp-fields-menu__check${item.done ? " is-done" : ""}`}
              onClick={() => onEditInitials?.()}
              disabled={disabled}
            >
              <span className="sp-fields-menu__check-mark">
                {item.done ? <CheckIcon /> : null}
              </span>
              {item.label}
            </button>
          ))}
          <button
            type="button"
            className={`sp-fields-menu__check${
              checklist.format.done ? " is-done" : ""
            }`}
            onClick={() => onChooseFormat?.()}
            disabled={disabled || !checklist.signatureReady}
          >
            <span className="sp-fields-menu__check-mark">
              {checklist.format.done ? <CheckIcon /> : null}
            </span>
            {checklist.format.label} *
          </button>
          {highlightSetup && !checklist.signatureReady ? (
            <div className="sp-fields-menu__nudge">
              <span className="sp-fields-menu__nudge-icon" aria-hidden="true">
                👆
              </span>
              <span>Start by creating your signature — it only takes a moment.</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {checklist.complete ? (
        <>
          {setupPhase ? <hr className="sp-fields-menu__divider" /> : null}
          <p className="sp-fields-menu__section-title">Add to document</p>
          <div className="sp-palette sp-fields-menu__palette" role="list">
            {SIGN_PDF_FIELD_OPTIONS.map((field) => {
              const isSig =
                field.id === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
                field.id === SIGN_PDF_FIELD_KINDS.INITIALS;
              const canPlace = fieldsEnabled && canPlaceField?.(field.id);
              const templateSettings = getSignPdfTemplate(session, field.id);
              return (
                <SignPdfFieldPaletteRow
                  key={field.id}
                  field={field}
                  templateSettings={templateSettings}
                  disabled={disabled || (!canPlace && !isSig)}
                  onPlace={onPlaceField}
                  onEdit={(kind) => {
                    if (kind === SIGN_PDF_FIELD_KINDS.INITIALS) {
                      onEditInitials?.();
                    } else {
                      onEditSignature?.();
                    }
                  }}
                />
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

function useAnchoredPosition(open, anchorRef, menuWidth = 200, anchorKey = null) {
  const [placement, setPlacement] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPlacement(null);
      return undefined;
    }
    const place = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const pad = 8;
      const anchorCenterX = rect.left + rect.width / 2;
      const left = Math.min(
        Math.max(anchorCenterX - menuWidth / 2, pad),
        window.innerWidth - menuWidth - pad
      );
      setPlacement({
        style: {
          position: "fixed",
          top: rect.bottom + 8,
          left,
          zIndex: 10050,
        },
        arrowLeft: anchorCenterX - left,
      });
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, anchorRef, menuWidth, anchorKey]);

  return placement;
}

function useDropdownPosition(open, anchorRef, menuWidth = FIELDS_MENU_WIDTH) {
  const [style, setStyle] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setStyle(null);
      return undefined;
    }
    const place = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const pad = 8;
      setStyle({
        position: "fixed",
        top: rect.bottom + 6,
        left: Math.min(
          Math.max(rect.left, pad),
          window.innerWidth - menuWidth - pad
        ),
        zIndex: 10050,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, anchorRef, menuWidth]);

  return style;
}

/**
 * Guided Sign toolbar — Fields + Signature blocks + page progress rail.
 */
const SignPdfSignToolbar = ({
  session,
  workflowStep = SIGN_PDF_WORKFLOW_STEPS.PLACE,
  disabled = false,
  pageCount = 0,
  currentPage = 1,
  maxPages = 50,
  pageUrls = {},
  pageConfirmedMap = {},
  allPagesConfirmed = false,
  allPagesComplete = false,
  onSelectPage = () => {},
  onPlaceField = () => {},
  onPlaceBlock = () => {},
  onBlockLocked = () => {},
  onBlocksLocked = () => {},
  canPlaceBlock = () => false,
  onEditSignature = () => {},
  onEditInitials = () => {},
  onChooseFormat = () => {},
  onMarkPageDone = () => {},
  onMarkAllDone = () => {},
  onGoReview = () => {},
  onFinishDownload = () => {},
  onFinishDownloadLater = () => {},
  onFinishPrint = () => {},
  onFinishEmailPdf = () => {},
  onFinishShare = () => {},
  finishCompleted = false,
  reviewMarkedComplete = false,
  onReviewMarkedComplete = () => {},
  shareSupported = false,
  busy = false,
  canPlaceField = () => true,
  autoOpenFields = false,
  ensurePageUrl = null,
  popdownReopenToken = 0,
  placementDragging = false,
  getPageRailState = null,
}) => {
  const fieldsBtnRef = useRef(null);
  const blocksBtnRef = useRef(null);
  const activePageBtnRef = useRef(null);
  const reviewBtnRef = useRef(null);
  const finishBtnRef = useRef(null);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [blocksOpen, setBlocksOpen] = useState(false);
  const [highlightSetup, setHighlightSetup] = useState(false);
  const [pagePopdownOpen, setPagePopdownOpen] = useState(false);
  const [reviewPopdownOpen, setReviewPopdownOpen] = useState(false);
  const [finishPopdownOpen, setFinishPopdownOpen] = useState(false);
  const [popdownSuppressed, setPopdownSuppressed] = useState(false);
  const fieldsMenuId = useId();
  const blocksMenuId = useId();
  const fieldsMenuStyle = useDropdownPosition(fieldsOpen, fieldsBtnRef, FIELDS_MENU_WIDTH);
  const blocksMenuStyle = useDropdownPosition(blocksOpen, blocksBtnRef, BLOCKS_MENU_WIDTH);
  const pagePopdownPlacement = useAnchoredPosition(
    pagePopdownOpen,
    activePageBtnRef,
    140,
    currentPage
  );
  const finishPopdownPlacement = useAnchoredPosition(
    finishPopdownOpen,
    finishBtnRef,
    168
  );
  const reviewPopdownPlacement = useAnchoredPosition(
    reviewPopdownOpen,
    reviewBtnRef,
    220
  );

  const resolveRailState = (pageNum) => {
    if (getPageRailState) return getPageRailState(pageNum);
    return getSignPdfPageRailState(session, pageNum, {
      currentPage,
      pageConfirmedMap,
      allPagesConfirmed,
    });
  };

  const railOpts = { currentPage, pageConfirmedMap, allPagesConfirmed };

  const checklist = getSignPdfSetupChecklist(session);
  const blocksUnlocked = isSignPdfBlocksUnlocked(session);
  const inReview = workflowStep === SIGN_PDF_WORKFLOW_STEPS.REVIEW;
  const inPlace = workflowStep === SIGN_PDF_WORKFLOW_STEPS.PLACE;
  const cappedCount = Math.min(pageCount, maxPages);
  const setupDone = checklist.complete;
  const reviewActive = inReview;
  const finishReady = reviewActive && reviewMarkedComplete;
  const reviewSpotlight = inReview && !reviewMarkedComplete;
  const finishSpotlight = finishReady && !finishCompleted;
  const reviewRailClass = !allPagesComplete
    ? "is-grey"
    : reviewMarkedComplete
      ? "is-green"
      : "is-yellow";
  const finishRailClass = finishCompleted
    ? "is-green"
    : finishReady
      ? "is-yellow"
      : "is-grey";
  const currentRailState = resolveRailState(currentPage);
  const shouldOfferPagePopdown =
    setupDone &&
    inPlace &&
    !inReview &&
    !popdownSuppressed &&
    !placementDragging &&
    !allPagesConfirmed &&
    currentRailState !== SIGN_PDF_RAIL_STATES.GREEN;

  useEffect(() => {
    if (shouldOfferPagePopdown) {
      setPagePopdownOpen(true);
    } else if (!fieldsOpen && !blocksOpen) {
      setPagePopdownOpen(false);
    }
  }, [shouldOfferPagePopdown, currentPage, fieldsOpen, blocksOpen]);

  useEffect(() => {
    if (popdownReopenToken > 0 && shouldOfferPagePopdown) {
      setPagePopdownOpen(true);
    }
  }, [popdownReopenToken, shouldOfferPagePopdown, currentPage]);

  useEffect(() => {
    if (!inReview) {
      setReviewPopdownOpen(false);
      return;
    }
    if (!reviewMarkedComplete) {
      setReviewPopdownOpen(true);
    }
  }, [inReview, reviewMarkedComplete]);

  useEffect(() => {
    if (!fieldsOpen && popdownSuppressed) {
      setPopdownSuppressed(false);
      if (setupDone && inPlace && !inReview) {
        setPagePopdownOpen(true);
      }
    }
  }, [fieldsOpen, popdownSuppressed, setupDone, inPlace, inReview]);

  useEffect(() => {
    if (!blocksOpen && popdownSuppressed) {
      setPopdownSuppressed(false);
      if (setupDone && inPlace && !inReview) {
        setPagePopdownOpen(true);
      }
    }
  }, [blocksOpen, popdownSuppressed, setupDone, inPlace, inReview]);

  useEffect(() => {
    if (!finishPopdownOpen) return undefined;
    const onDoc = (e) => {
      if (
        finishBtnRef.current?.contains(e.target) ||
        e.target.closest?.(".sp-finish-popdown")
      ) {
        return;
      }
      setFinishPopdownOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [finishPopdownOpen]);

  useEffect(() => {
    if (!inReview) setFinishPopdownOpen(false);
  }, [inReview]);

  useEffect(() => {
    if (!reviewMarkedComplete) setFinishPopdownOpen(false);
  }, [reviewMarkedComplete]);

  useEffect(() => {
    if (!autoOpenFields || disabled) return;
    setFieldsOpen(true);
    setHighlightSetup(true);
    const t = setTimeout(() => setHighlightSetup(false), 8000);
    return () => clearTimeout(t);
  }, [autoOpenFields, disabled]);

  useEffect(() => {
    if (!fieldsOpen) return undefined;
    const onDoc = (e) => {
      if (
        fieldsBtnRef.current?.contains(e.target) ||
        e.target.closest?.(".sp-fields-menu")
      ) {
        return;
      }
      setFieldsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [fieldsOpen]);

  useEffect(() => {
    if (!blocksOpen) return undefined;
    const onDoc = (e) => {
      if (
        blocksBtnRef.current?.contains(e.target) ||
        e.target.closest?.(".sp-blocks-panel--flyout")
      ) {
        return;
      }
      setBlocksOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [blocksOpen]);

  const handleBlocksClick = () => {
    if (!blocksUnlocked) {
      onBlocksLocked?.();
      setBlocksOpen(false);
      setFieldsOpen(true);
      setHighlightSetup(true);
      return;
    }
    setFieldsOpen(false);
    setBlocksOpen((v) => !v);
  };

  const openFieldsFromToolbar = () => {
    setBlocksOpen(false);
    setFieldsOpen((v) => !v);
  };

  const openFieldsFromPopdown = () => {
    setPopdownSuppressed(true);
    setFieldsOpen(true);
  };

  const openBlocksFromPopdown = () => {
    if (!blocksUnlocked) {
      onBlocksLocked?.();
      return;
    }
    setFieldsOpen(false);
    setBlocksOpen(true);
  };

  const handlePlaceBlock = (block, sizeId, meta) => {
    onPlaceBlock?.(block, sizeId, meta);
    setBlocksOpen(false);
    setPopdownSuppressed(false);
  };

  const canDone = canConfirmCurrentPage(session, currentPage, railOpts);
  const canDoneAll = canDone;
  const pages = [];
  for (let p = 1; p <= cappedCount; p += 1) {
    pages.push(p);
  }

  return (
    <div className="sp-sign-toolbar" role="region" aria-label="Sign workflow">
      <div className="sp-sign-toolbar__row">
        <div className="sp-sign-toolbar__tools">
          <button
            ref={fieldsBtnRef}
            type="button"
            className={`sp-sign-toolbar__btn${
              fieldsOpen ? " is-active" : ""
            }`}
            disabled={disabled}
            aria-expanded={fieldsOpen}
            aria-controls={fieldsMenuId}
            onClick={openFieldsFromToolbar}
          >
            Signature Fields
            {!checklist.complete ? (
              <span className="sp-sign-toolbar__btn-meta">
                {checklist.doneCount}/{checklist.totalRequired}
              </span>
            ) : null}
          </button>

          <button
            ref={blocksBtnRef}
            type="button"
            className={`sp-sign-toolbar__btn${
              blocksOpen ? " is-active" : ""
            }${!blocksUnlocked ? " is-locked" : ""}`}
            disabled={disabled}
            aria-expanded={blocksOpen}
            aria-controls={blocksMenuId}
            onClick={handleBlocksClick}
            title={
              blocksUnlocked
                ? "Browse signature blocks"
                : "Complete your signature first"
            }
          >
            Signature blocks
          </button>
        </div>

        {cappedCount > 0 ? (
          <div className="sp-sign-toolbar__progress-wrap">
            <div className="sp-sign-rail sp-sign-toolbar__progress" role="list">
            <div className="sp-sign-rail__node" role="listitem">
              <button
                type="button"
                className={`sp-sign-rail__btn${
                  workflowStep === SIGN_PDF_WORKFLOW_STEPS.SETUP
                    ? " is-active"
                    : ""
                }${setupDone ? " is-green" : ""}`}
                onClick={() => {
                  setFieldsOpen(true);
                  onEditSignature?.();
                }}
                disabled={disabled}
              >
                <span
                  className={`sp-sign-rail__thumb sp-sign-rail__thumb--start${
                    setupDone ? " is-green" : " is-grey"
                  }`}
                >
                  {setupDone ? "✓" : "★"}
                </span>
                <span
                  className={`sp-sign-rail__label${
                    setupDone ? " is-green" : ""
                  }`}
                >
                  Start
                </span>
              </button>
            </div>

            {pages.map((p) => {
              const railState = resolveRailState(p);
              const railClass = railStateClassName(railState);
              const isActive = p === currentPage && !inReview;
              const pageSpotlight = shouldSpotlightPageThumb(session, p, {
                currentPage,
                pageConfirmedMap,
                allPagesConfirmed,
                inPlace,
                inReview,
              });
              const url = pageUrls[p];
              if (!url && ensurePageUrl) ensurePageUrl(p);

              return (
                <React.Fragment key={p}>
                  <span
                    className={`sp-sign-rail__connector ${connectorClassForRailState(
                      railState
                    )}`}
                    aria-hidden="true"
                  />
                  <div className="sp-sign-rail__node" role="listitem">
                    <button
                      ref={isActive ? activePageBtnRef : null}
                      type="button"
                      className={`sp-sign-rail__btn${isActive ? " is-active" : ""} ${railClass}`}
                      onClick={() => onSelectPage?.(p)}
                      disabled={disabled}
                    >
                      <RailThumbFrame
                        spotlight={pageSpotlight ? "page" : null}
                      >
                        <span className={`sp-sign-rail__thumb ${railClass}`}>
                          {url ? (
                            <img src={url} alt="" draggable={false} />
                          ) : (
                            <span className="sp-sign-rail__thumb-fallback">
                              {p}
                            </span>
                          )}
                        </span>
                      </RailThumbFrame>
                      <span className={`sp-sign-rail__label ${railClass}`}>
                        P{p}
                      </span>
                    </button>
                  </div>
                </React.Fragment>
              );
            })}

            <span
              className={`sp-sign-rail__connector ${
                allPagesComplete ? "is-done" : ""
              }`}
              aria-hidden="true"
            />
            <div className="sp-sign-rail__node" role="listitem">
              <button
                ref={reviewBtnRef}
                type="button"
                className={`sp-sign-rail__btn${
                  reviewActive ? " is-active" : ""
                } ${reviewRailClass}`}
                onClick={() => {
                  if (!reviewActive) {
                    onGoReview?.();
                    return;
                  }
                  if (!reviewMarkedComplete) {
                    setReviewPopdownOpen((v) => !v);
                  }
                }}
                disabled={disabled || !allPagesComplete}
              >
                <RailThumbFrame spotlight={reviewSpotlight ? "review" : null}>
                  <span
                    className={`sp-sign-rail__thumb sp-sign-rail__thumb--review ${reviewRailClass}`}
                  >
                    <ReviewEyeIcon />
                  </span>
                </RailThumbFrame>
                <span className={`sp-sign-rail__label ${reviewRailClass}`}>
                  {reviewMarkedComplete ? "Reviewed" : "Review"}
                </span>
              </button>
            </div>

            <span
              className={`sp-sign-rail__connector${
                finishReady || finishCompleted ? " is-done" : ""
              }`}
              aria-hidden="true"
            />
            <div className="sp-sign-rail__node" role="listitem">
              <button
                ref={finishBtnRef}
                type="button"
                className={`sp-sign-rail__btn${
                  finishReady && !finishCompleted ? " is-active is-finish-ready" : ""
                } ${finishRailClass}`}
                onClick={() => {
                  if (!finishReady) return;
                  setFinishPopdownOpen((v) => !v);
                }}
                disabled={disabled || !finishReady}
                aria-expanded={finishPopdownOpen}
              >
                <RailThumbFrame spotlight={finishSpotlight ? "finish" : null}>
                  <span
                    className={`sp-sign-rail__thumb sp-sign-rail__thumb--finish ${finishRailClass}`}
                  >
                    {finishCompleted ? "✓" : "↓"}
                  </span>
                </RailThumbFrame>
                <span
                  className={`sp-sign-rail__label${
                    finishCompleted
                      ? " is-green"
                      : finishReady
                        ? " is-finish-ready"
                        : " is-grey"
                  }`}
                >
                  {finishCompleted ? "Finished" : "Finish"}
                </span>
              </button>
            </div>
            </div>
          </div>
        ) : null}
      </div>

      {pagePopdownOpen &&
      pagePopdownPlacement &&
      typeof document !== "undefined"
        ? createPortal(
            <div
              className="sp-rail-popdown-wrap"
              style={{
                ...pagePopdownPlacement.style,
                "--sp-popdown-arrow-left": `${pagePopdownPlacement.arrowLeft}px`,
              }}
            >
              <SignPdfPagePopdown
                pageNum={currentPage}
                session={session}
                toolsDisabled={disabled}
                canDone={canDone}
                canDoneAll={canDoneAll}
                onDone={() => {
                  onMarkPageDone?.(currentPage);
                }}
                onDoneAll={() => {
                  onMarkAllDone?.();
                }}
                onAddFields={openFieldsFromPopdown}
                onAddBlock={openBlocksFromPopdown}
              />
            </div>,
            document.body
          )
        : null}

      {reviewPopdownOpen &&
      reviewPopdownPlacement &&
      typeof document !== "undefined"
        ? createPortal(
            <div
              className="sp-rail-popdown-wrap"
              style={{
                ...reviewPopdownPlacement.style,
                "--sp-popdown-arrow-left": `${reviewPopdownPlacement.arrowLeft}px`,
              }}
            >
              <SignPdfReviewPopdown
                disabled={disabled}
                onCompleted={() => {
                  onReviewMarkedComplete(true);
                  setReviewPopdownOpen(false);
                  setFinishPopdownOpen(true);
                }}
              />
            </div>,
            document.body
          )
        : null}

      {finishPopdownOpen &&
      finishPopdownPlacement &&
      finishReady &&
      typeof document !== "undefined"
        ? createPortal(
            <div
              className="sp-rail-popdown-wrap"
              style={{
                ...finishPopdownPlacement.style,
                "--sp-popdown-arrow-left": `${finishPopdownPlacement.arrowLeft}px`,
              }}
            >
              <SignPdfFinishPopdown
                disabled={disabled}
                busy={busy}
                shareSupported={shareSupported}
                onDownload={() => {
                  setFinishPopdownOpen(false);
                  onFinishDownload?.();
                }}
                onDownloadLater={() => {
                  setFinishPopdownOpen(false);
                  onFinishDownloadLater?.();
                }}
                onPrint={() => {
                  setFinishPopdownOpen(false);
                  onFinishPrint?.();
                }}
                onEmailPdf={() => {
                  setFinishPopdownOpen(false);
                  onFinishEmailPdf?.();
                }}
                onShare={() => {
                  setFinishPopdownOpen(false);
                  onFinishShare?.();
                }}
              />
            </div>,
            document.body
          )
        : null}

      {fieldsOpen && fieldsMenuStyle && typeof document !== "undefined"
        ? createPortal(
            <div id={fieldsMenuId} style={fieldsMenuStyle}>
              <FieldsMenu
                session={session}
                workflowStep={workflowStep}
                disabled={disabled}
                onEditSignature={() => {
                  setFieldsOpen(false);
                  onEditSignature?.();
                }}
                onEditInitials={() => {
                  setFieldsOpen(false);
                  onEditInitials?.();
                }}
                onChooseFormat={() => {
                  setFieldsOpen(false);
                  onChooseFormat?.();
                }}
                onPlaceField={(kind, patch) => {
                  onPlaceField?.(kind, patch);
                  setFieldsOpen(false);
                  setPopdownSuppressed(false);
                }}
                canPlaceField={canPlaceField}
                highlightSetup={highlightSetup}
              />
            </div>,
            document.body
          )
        : null}

      {blocksOpen && blocksMenuStyle && typeof document !== "undefined"
        ? createPortal(
            <div id={blocksMenuId} style={blocksMenuStyle}>
              <SignPdfBlocksPanel
                open
                variant="flyout"
                disabled={disabled}
                canPlace={canPlaceBlock()}
                onPlace={handlePlaceBlock}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SignPdfSignToolbar;
